import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, ExternalLink, X } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { authApi, formatApiError } from "@/api";

const EMPTY = {
  title: "", category: "video", embed_type: "vimeo", embed_id: "",
  poster: "", preview: "", year: "", client: "", role_en: "", role_tr: "",
  desc_en: "", desc_tr: "", span: "lg-6", order: 0,
};

const label = "block font-mono text-[10px] tracking-[0.2em] uppercase text-bone/40 mb-2";
const field = "w-full bg-surface border border-border rounded px-3 py-2.5 font-body text-bone outline-none focus:border-navy-ink transition-colors";

function Field({ id, name, value, onChange, placeholder, textarea }) {
  return (
    <div>
      <label className={label}>{name}</label>
      {textarea ? (
        <textarea data-testid={`pf-${id}`} value={value} onChange={onChange} rows={3} className={`${field} resize-none`} placeholder={placeholder} />
      ) : (
        <input data-testid={`pf-${id}`} value={value} onChange={onChange} className={field} placeholder={placeholder} />
      )}
    </div>
  );
}

function ProjectForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({ ...f, order: Number(f.order) || 0 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-5"
      data-testid="project-form"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field id="title" name="Title" value={f.title} onChange={set("title")} />
        <div>
          <label className={label}>Category</label>
          <select data-testid="pf-category" value={f.category} onChange={set("category")} className={field}>
            <option value="motion">Motion</option>
            <option value="video">Video</option>
            <option value="graphic">Graphic</option>
          </select>
        </div>
        <div>
          <label className={label}>Embed type</label>
          <select data-testid="pf-embed_type" value={f.embed_type} onChange={set("embed_type")} className={field}>
            <option value="vimeo">Vimeo</option>
            <option value="youtube">YouTube</option>
          </select>
        </div>
        <Field id="embed_id" name="Video URL or ID" value={f.embed_id} onChange={set("embed_id")} placeholder="https://vimeo.com/123456789" />
        <Field id="poster" name="Poster image URL" value={f.poster} onChange={set("poster")} placeholder="https://..." />
        <Field id="preview" name="Preview video/GIF URL" value={f.preview} onChange={set("preview")} placeholder="https://....mp4" />
        <Field id="client" name="Client" value={f.client} onChange={set("client")} />
        <Field id="year" name="Year" value={f.year} onChange={set("year")} />
        <Field id="role_en" name="Role (EN)" value={f.role_en} onChange={set("role_en")} />
        <Field id="role_tr" name="Role (TR)" value={f.role_tr} onChange={set("role_tr")} />
        <div>
          <label className={label}>Card size</label>
          <select data-testid="pf-span" value={f.span} onChange={set("span")} className={field}>
            <option value="lg-12">Full width</option>
            <option value="lg-8">Large</option>
            <option value="lg-6">Medium</option>
            <option value="lg-4">Small</option>
          </select>
        </div>
        <Field id="order" name="Order" value={f.order} onChange={set("order")} />
      </div>
      <Field id="desc_en" name="Description (EN)" value={f.desc_en} onChange={set("desc_en")} textarea />
      <Field id="desc_tr" name="Description (TR)" value={f.desc_tr} onChange={set("desc_tr")} textarea />

      <div className="flex items-center gap-3 pt-2">
        <button type="submit" disabled={saving} data-testid="save-project" className="rounded-full bg-navy px-6 py-3 font-mono text-[11px] tracking-[0.2em] uppercase text-white hover:bg-navy-ink transition-colors disabled:opacity-50">
          {saving ? "Saving..." : "Save project"}
        </button>
        <button type="button" onClick={onCancel} data-testid="cancel-form" className="rounded-full border border-border px-6 py-3 font-mono text-[11px] tracking-[0.2em] uppercase text-bone/60 hover:text-bone transition-colors">
          Cancel
        </button>
      </div>
    </motion.form>
  );
}

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [messages, setMessages] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} (new) | project (edit)

  const load = useCallback(async () => {
    try {
      const [p, m] = await Promise.all([
        authApi.get("/admin/projects"),
        authApi.get("/admin/messages"),
      ]);
      setProjects(p.data);
      setMessages(m.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    if (admin) load();
  }, [admin, load]);

  const save = async (data) => {
    try {
      if (editing && editing.id) {
        await authApi.put(`/admin/projects/${editing.id}`, data);
        toast.success("Project updated");
      } else {
        await authApi.post("/admin/projects", data);
        toast.success("Project created");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await authApi.delete(`/admin/projects/${id}`);
      toast.success("Project deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const delMsg = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await authApi.delete(`/admin/messages/${id}`);
      setMessages((m) => m.filter((x) => x.id !== id));
      toast.success("Message deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-void" data-testid="admin-dashboard">
      <div className="noise-overlay" />
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-border bg-void/80 backdrop-blur-xl">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="h-2 w-2 rounded-full bg-navy-ink animate-pulse" />
            <span className="font-mono text-xs tracking-[0.3em] uppercase text-bone">Studio Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block font-mono text-[10px] tracking-[0.15em] text-bone/40">{admin?.email}</span>
            <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-bone/60 hover:text-navy-ink transition-colors">
              <ExternalLink size={14} /> Site
            </a>
            <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-2 rounded-full border border-border px-4 py-2 font-mono text-[10px] tracking-[0.2em] uppercase text-bone/70 hover:border-navy-ink hover:text-navy-ink transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 md:px-10 py-12">
        {/* Tabs */}
        <div className="flex items-center gap-3 mb-10">
          {[
            { k: "projects", l: `Projects (${projects.length})` },
            { k: "messages", l: `Messages (${messages.length})` },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => { setTab(t.k); setEditing(null); }}
              data-testid={`tab-${t.k}`}
              className={`rounded-full border px-5 py-2.5 font-mono text-[11px] tracking-[0.2em] uppercase transition-colors ${
                tab === t.k ? "border-navy bg-navy text-white" : "border-border text-bone/50 hover:text-bone"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {tab === "projects" && (
          <>
            <AnimatePresence mode="wait">
              {editing !== null ? (
                <motion.div key="form" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="rounded-xl border border-border bg-surface/40 p-6 md:p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-display text-3xl font-semibold uppercase text-bone">
                      {editing.id ? "Edit project" : "New project"}
                    </h2>
                    <button onClick={() => setEditing(null)} className="text-bone/50 hover:text-bone"><X /></button>
                  </div>
                  <ProjectForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />
                </motion.div>
              ) : (
                <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <button onClick={() => setEditing({})} data-testid="new-project-btn" className="mb-8 flex items-center gap-2 rounded-full bg-navy px-6 py-3 font-mono text-[11px] tracking-[0.2em] uppercase text-white hover:bg-navy-ink transition-colors">
                    <Plus size={16} /> New project
                  </button>
                  <div className="space-y-3">
                    {projects.map((p) => (
                      <div key={p.id} data-testid={`project-row-${p.slug}`} className="flex items-center gap-4 rounded-lg border border-border bg-surface/30 p-4 hover:border-bone/20 transition-colors">
                        <img src={p.poster} alt="" className="h-16 w-24 rounded object-cover grayscale" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-navy-ink">{p.category}</span>
                            <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-bone/30">#{p.order}</span>
                          </div>
                          <h3 className="font-display text-xl font-medium uppercase text-bone truncate">{p.title}</h3>
                          <p className="font-mono text-[10px] tracking-[0.1em] text-bone/40 truncate">{p.embed_type} · {p.embed_id || "—"}</p>
                        </div>
                        <button onClick={() => setEditing(p)} data-testid={`edit-${p.slug}`} className="flex h-10 w-10 items-center justify-center rounded-full border border-border text-bone/60 hover:text-navy-ink hover:border-navy-ink transition-colors"><Pencil size={15} /></button>
                        <button onClick={() => del(p.id)} data-testid={`delete-${p.slug}`} className="flex h-10 w-10 items-center justify-center rounded-full border border-border text-bone/60 hover:text-vermilion hover:border-vermilion transition-colors"><Trash2 size={15} /></button>
                      </div>
                    ))}
                    {projects.length === 0 && <p className="font-mono text-sm text-bone/40 py-10 text-center uppercase tracking-widest">No projects yet.</p>}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        {tab === "messages" && (
          <div className="space-y-3">
            {messages.map((m) => (
              <div key={m.id} data-testid="message-row" className="rounded-lg border border-border bg-surface/30 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl font-medium text-bone">{m.name}</h3>
                    <a href={`mailto:${m.email}`} className="font-mono text-[11px] tracking-[0.1em] text-navy-ink hover:underline">{m.email}</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[10px] text-bone/30">{(m.created_at || "").slice(0, 10)}</span>
                    <button onClick={() => delMsg(m.id)} data-testid="delete-message" className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-bone/50 hover:text-vermilion hover:border-vermilion transition-colors"><Trash2 size={14} /></button>
                  </div>
                </div>
                <p className="mt-3 font-body font-light text-bone/70 leading-relaxed whitespace-pre-wrap">{m.message}</p>
              </div>
            ))}
            {messages.length === 0 && <p className="font-mono text-sm text-bone/40 py-10 text-center uppercase tracking-widest">No messages yet.</p>}
          </div>
        )}
      </main>
    </div>
  );
}
